import styled from "styled-components";
import { useEffect, useState } from "react";
import { gql, useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PurchaseCoins from "./PurchaseCoins";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import Backdrop from "components/other/Backdrop";
import MessageWindow from "components/other/MessageWindow";

const ModalWrap = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
`;

export default () => {
  const navigate = useNavigate();

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [billing, setBilling] = useState({ name: "" });
  const [countrySelectionOpened, setCountrySelectionOpened] = useState(false);
  const [countrySelected, setCountrySelected] = useState(null);
  const [enteredBilling, setEnteredBilling] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [convertCashLoading, setConvertCashLoading] = useState(false);
  const [errorWindow, setErrorWindow] = useState(false);

  // const UserProperties = createSelector(
  //   (state) => state.User,
  //   (user) => ({
  //     profileData: user.profileData,
  //   })
  // );

  // const { profileData } = useSelector(UserProperties);

  // Fetch data
  const GET_BILLING_DATA = gql`
    query GetBillingData {
      billingData {
        billingAddress {
          id
          name
          country
        }
        prices {
          product
          price
        }
        vatRate
        promo {
          expiration
          promo
          data
        }
        availableCashBalance
      }
    }
  `;

  const { data, error } = useQuery(GET_BILLING_DATA, {
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("Fetching billing data error", error);
    captureException(error);
  }

  // handle Package selection
  const selectPackage = (coins) => {
    setSelectedPackage(coins);
  };

  useEffect(() => {
    if (data && !selectedPackage) {
      const firstPackage = data.billingData.prices[0].product;
      const coins = Number(firstPackage.split("_")[0]);

      setSelectedPackage(coins);
    }
  }, [data]);

  // handle Billing address input
  const handleInputChange = (e) => {
    let newBilling = { ...billing };
    newBilling[e.target.name] = e.target.value;

    setBilling(newBilling);
  };

  const toggleCountrySelection = () => {
    setCountrySelectionOpened((prevState) => !prevState);
  };

  const onCountrySelect = (country) => {
    setCountrySelected(country);
    setCountrySelectionOpened(false);

    const newWarnings = warnings.filter(
      (warning) => warning !== "billingCountry"
    );

    setWarnings(newWarnings);
  };

  const billingFormContinue = () => {
    setWarnings([]);

    let newWarnings = [];

    if (billing.name.trim().length < 5) newWarnings.push("billingName");
    if (!countrySelected) newWarnings.push("billingCountry");

    if (newWarnings.length > 0) {
      setWarnings(newWarnings);
      return;
    }

    setEnteredBilling(true);
    setTimeout(
      () =>
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        }),
      100
    );
  };

  const billingFormEdit = () => {
    setEnteredBilling(false);
  };

  // Get Stripe checkout URL
  const GET_STRIPE_CHECKOUT = gql`
    query GetStripeCheckout($pack: String!) {
      getStripeCheckout(pack: $pack) {
        url
      }
    }
  `;

  const [getStripeCheckout] = useLazyQuery(GET_STRIPE_CHECKOUT);

  // handle Stripe checkout
  const stripeCheckout = async () => {
    try {
      if (checkoutLoading) return;
      if (!selectedPackage) return;

      setCheckoutLoading(true);

      const packageName = selectedPackage + "_coins";

      // create checkout session
      const checkout = await getStripeCheckout({
        variables: {
          pack: packageName,
        },
        fetchPolicy: "network-only",
      });

      const { loading, error, data } = checkout;

      setCheckoutLoading(false);

      if (error) {
        console.log("Get stripe checkout error", error);
        captureException(error);
      }

      if (data && !loading) {
        if (data.getStripeCheckout?.url) {
          window.location = data.getStripeCheckout.url;
        }
      }
    } catch (error) {
      captureException(error);
      setCheckoutLoading(false);
      return;
    }
  };

  // Get Confirmo checkout URL
  const GET_CONFIRMO_CHECKOUT = gql`
    query GetConfirmoCheckout($pack: String!) {
      getConfirmoCheckout(pack: $pack) {
        url
      }
    }
  `;

  const [getConfirmoCheckout] = useLazyQuery(GET_CONFIRMO_CHECKOUT);

  // handle Confirmo checkout
  const confirmoCheckout = async () => {
    try {
      if (checkoutLoading) return;
      if (!selectedPackage) return;

      setCheckoutLoading(true);

      const packageName = selectedPackage + "_coins";

      // create checkout session
      const checkout = await getConfirmoCheckout({
        variables: {
          pack: packageName,
        },
        fetchPolicy: "network-only",
      });

      const { loading, error, data } = checkout;

      setCheckoutLoading(false);

      if (data.getConfirmoCheckout.url) {
        window.location = data.getConfirmoCheckout.url;
      }

      if (error) {
        console.log("Get confirmo checkout error", error);
        captureException(error);
      }

      if (data && !loading) {
        if (data.getConfirmoCheckout?.url) {
          window.location = data.getConfirmoCheckout.url;
        }
      }
    } catch (error) {
      captureException(error);
      setCheckoutLoading(false);
      return;
    }
  };

  // // Get Coingate checkout URL
  // const GET_COINGATE_CHECKOUT = gql`
  //   query GetCoingateCheckout($pack: String!) {
  //     getCoingateCheckout(pack: $pack) {
  //       url
  //     }
  //   }
  // `;

  // const [getCoingateCheckout] = useLazyQuery(GET_COINGATE_CHECKOUT);

  // // handle Coingate checkout
  // const coingateCheckout = async () => {
  //   try {
  //     if (checkoutLoading) return;
  //     if (!selectedPackage) return;

  //     setCheckoutLoading(true);

  //     const packageName = selectedPackage + "_coins";

  //     // create checkout session
  //     const checkout = await getCoingateCheckout({
  //       variables: {
  //         pack: packageName,
  //       },
  //       fetchPolicy: "network-only",
  //     });

  //     const { loading, error, data } = checkout;

  //     setCheckoutLoading(false);

  //     if (error) {
  //       console.log("Get coingate checkout error", error);
  //       captureException(error);
  //     }

  //     if (data && !loading) {
  //       if (data.getCoingateCheckout?.url) {
  //         //console.log("Will redirect to", data.getCoingateCheckout.url);
  //         //window.location = data.getCoingateCheckout.url;
  //       }
  //     }
  //   } catch (error) {
  //     captureException(error);
  //     setCheckoutLoading(false);
  //     return;
  //   }
  // };

  // Get Stripe checkout URL
  const GET_PAYPAL_CHECKOUT = gql`
    query GetPaypalCheckout($pack: String!) {
      getPaypalCheckout(pack: $pack) {
        id
      }
    }
  `;

  const [getPaypalCheckout] = useLazyQuery(GET_PAYPAL_CHECKOUT);

  // handle PayPal checkout
  const onPaypalApprove = () => {
    navigate("/wallet?paypalApproved=true");
  };

  const paypalCheckout = async () => {
    try {
      if (checkoutLoading) return;
      if (!selectedPackage) return;

      setCheckoutLoading(true);

      const packageName = selectedPackage + "_coins";

      // create checkout session
      const checkout = await getPaypalCheckout({
        variables: {
          pack: packageName,
        },
        fetchPolicy: "network-only",
      });

      const { loading, error, data } = checkout;

      console.log("checkout data", data);

      setCheckoutLoading(false);

      if (error) {
        console.log("Get paypal checkout error", error);
        captureException(error);
      }

      return data.getPaypalCheckout.id;
    } catch (error) {
      captureException(error);
      setCheckoutLoading(false);
      return null;
    }
  };

  const stripeDisabled = false;
  const confirmoDisabled = false;
  const paypalDisabled = true;

  // Error handling
  const openErrorWindow = () => {
    setErrorWindow(true);
  };

  const handleModalClose = (modal) => {
    if (modal === "error") {
      setErrorWindow(false);
    }
  };

  const onPaypalError = () => {
    openErrorWindow();
  };

  // handle Cash conversion
  const CONVERT_CASH = gql`
    mutation ConvertCash($product: String!) {
      convertCash(product: $product) {
        resultCode
      }
    }
  `;

  const [convertCash] = useMutation(CONVERT_CASH);

  const onConvertCash = async () => {
    if (convertCashLoading) return;

    setConvertCashLoading(true);

    const result = await convertCash({
      variables: { product: selectedPackage + "_coins" },
    });

    const { resultCode } = result.data.convertCash;
    //console.log({ resultCode });

    if (resultCode !== 0) {
      setConvertCashLoading(false);
      openErrorWindow();
      return;
    }

    navigate("/wallet");

    setConvertCashLoading(false);
  };

  // console.log("data", data);
  // console.log("selectedPackage", selectedPackage);

  return (
    <>
      {!data && (
        <LoadingBox>
          <BoxLoadingSpinner height="230px" />
        </LoadingBox>
      )}
      {data && (
        <PurchaseCoins
          data={data}
          selectPackage={selectPackage}
          selectedPackage={selectedPackage}
          warnings={warnings}
          handleInputChange={handleInputChange}
          billing={billing}
          toggleCountrySelection={toggleCountrySelection}
          countrySelectionOpened={countrySelectionOpened}
          onCountrySelect={onCountrySelect}
          countrySelected={countrySelected}
          billingFormContinue={billingFormContinue}
          enteredBilling={enteredBilling}
          billingFormEdit={billingFormEdit}
          stripeDisabled={stripeDisabled}
          confirmoDisabled={confirmoDisabled}
          stripeCheckout={stripeCheckout}
          confirmoCheckout={confirmoCheckout}
          // coingateCheckout={coingateCheckout}
          onConvertCash={onConvertCash}
          paypalCheckout={paypalCheckout}
          paypalDisabled={paypalDisabled}
          onPaypalApprove={onPaypalApprove}
          onPaypalError={onPaypalError}
        />
      )}
      {errorWindow && (
        <ModalWrap>
          <Backdrop
            onClick={handleModalClose}
            parameters="error"
            background="rgba(0, 0, 0, 0.7)"
          />
          <MessageWindow handleModalClose={handleModalClose} type="error" />
        </ModalWrap>
      )}
    </>
  );
};
